import { defineStore } from 'pinia';
import type { RemovableRef } from '@vueuse/core';
import { useStorage } from '@vueuse/core';
import type { StudioDisplayModel } from '@shared/models/studios/studios';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import type { identifier } from '@core/models/base';
import { isNullOrUndefined } from '@shared/utils';
import type { ServiceError } from '@core/models/actionResponse';
import { isServiceError, isServiceSuccess } from '@core/models/actionResponse';
import type { StudioJobPreferenceDisplayModel } from '@shared/models/jobs/jobPreferences';
import type { GreenScreenBackgroundDisplayModel } from '@shared/models/packages/greenScreenBackgrounds';
import { fetchGreenScreens } from '@apps/customer-package-builder/services/greenscreen.service';
import { getStudioDetails } from '../services/studio.service';
import { fetchJobDetails } from '../services/jobs.service';
import { getJobPreferences } from '../services/preferences.service';
import { JobOrderMethod } from '@shared/models/subjects/accessCode';
import type {
  JobDisplayModelV3,
  JobDisplayModelV3GallerySettings,
  JobDisplayModelV3PrepaySettings,
} from '@shared/models/jobs/v3/displayModels';
import { useCartStore } from './cart.store';

export interface CustomerState {
  job?: JobDisplayModelV3;
  greenScreens: GreenScreenBackgroundDisplayModel[];
  studio?: StudioDisplayModel;
  subject?: SubjectDisplayModel;
  studioSlug: string | undefined;
  loading: boolean;
  accessCode: RemovableRef<string | null>;
  cartStatus?: boolean;
  persistedJobId: RemovableRef<identifier | null>;
  persistedStudioId: RemovableRef<identifier | null>;
  jobPreferences?: StudioJobPreferenceDisplayModel;
  subjectImageCount: number;
}
export const useCustomerStore = defineStore('customerStore', {
  state: (): CustomerState => ({
    job: undefined,
    studio: undefined,
    subject: undefined,
    studioSlug: undefined,
    jobPreferences: undefined,
    loading: false,
    cartStatus: undefined,
    greenScreens: [],
    accessCode: useStorage<string>('customer-accesssCode', null),
    persistedJobId: useStorage<identifier>('customer-jobId', null),
    persistedStudioId: useStorage<identifier>('customer-studioId', null),
    subjectImageCount: 0,
  }),
  getters: {
    jobId: (state): number => state.job?.id || 0,
    studioId: (state): number => state.studio?.id || state.persistedStudioId || 0,
    /**
     * RS+ Hosted Job
     */
    isHosted(state): boolean {
      const cartStore = useCartStore();
      return cartStore.persistedOrderMethod === 'Gallery';
    },
    /**
     * RS+ Prepay Job
     */
    isPrePay(state): boolean {
      const cartStore = useCartStore();
      return cartStore.persistedOrderMethod === 'Prepay';
    },
    /**
     * Returns true if the job is prepay and was created inside RSPO
     */
    isPrePayRspo(state): boolean {
      return !!(state.job?.rspoJob && this.isPrePay);
    },
    /**
     * Returns true if the job is hosted and was created inside RSPO
     */
    isHostedRspo(state): boolean {
      return !!(state.job?.rspoJob && this.isHosted);
    },
    /**
     * Returns true if the job has green screens
     */
    hasGreenScreens: (state): boolean => !!state.greenScreens.length,

    prepaySettings(state): JobDisplayModelV3PrepaySettings | undefined {
      return state.job?.prepaySettings;
    },

    gallerySettings(state): JobDisplayModelV3GallerySettings | undefined {
      return state.job?.gallerySettings;
    },

    /**
     * Returns true if the job is prepay and imported data
     */
    hasImportedData(state): boolean {
      const version = state.job?.version;

      if (version && version.major >= 3) {
        return state.job?.importedData ?? false;
      } else {
        return this.prepaySettings?.importedData ?? false;
      }
    },

    /**
     * Hosted studio has enabled yearbook pose selection and selection time has not expired
     */
    canSelectYearBookPose(state): boolean {
      return !!(
        this.gallerySettings?.enableYearBookPoseSelection &&
        this.gallerySettings?.isYearBookPoseSelectionExpired !== true &&
        state.subjectImageCount > 1
      );
    },
  },
  actions: {
    persistState(jobId: identifier, accessCode: string | null, studioId: identifier | null) {
      this.accessCode = accessCode;
      this.persistedJobId = jobId;
      this.persistedStudioId = studioId;
    },
    clearPersistedState() {
      this.accessCode = null;
      this.persistedJobId = null;
      this.persistedStudioId = null;
    },
    setSubjectImageCount(count: number) {
      this.subjectImageCount = count;
    },
    async initialize(studioSlug: string, jobId?: identifier | null): Promise<ServiceError | null> {
      this.loading = true;
      if (this.studioSlug !== studioSlug) {
        this.studioSlug = studioSlug as string;
        const details = await getStudioDetails(this.studioSlug);
        if (isServiceError(details)) return details;
        if (!details.data.rspoEnabled) return { errors: ['There was an error loading the studio'] };
        this.studio = details.data;
      }

      if (this.studio && !isNullOrUndefined(jobId) && this.jobId !== jobId) {
        this.job = await fetchJobDetails(jobId as number);
        this.jobPreferences = (await getJobPreferences(this.studio?.id, this.job.id)).data;
        const gsResult = await fetchGreenScreens(jobId as number);
        if (isServiceSuccess(gsResult)) this.greenScreens = gsResult.data;
      } else if (isNullOrUndefined(jobId) && this.jobId > 0) {
        this.clearJob();
      }

      setTimeout(() => {
        this.loading = false;
      }, 1000);

      return null;
    },
    async resetJob() {
      if (this.job) {
        this.job = await fetchJobDetails(this.jobId);
      }
    },
    clearJob() {
      this.job = undefined;
      this.jobPreferences = undefined;
      this.greenScreens = [];
      this.subjectImageCount = 0;
    },
    clear() {
      this.clearJob();
      this.studio = undefined;
      this.subject = undefined;
      this.studioSlug = undefined;
      this.subjectImageCount = 0;
    },
  },
});
